import {
    Prospect,
    DetailedCampaignProspect,
    CampaignProspectStatus,
} from '../../../models/Outreach/types'

export type PipelineStage = 'prospect' | 'lead' | 'engaged' | 'converted'

export interface EnrichmentScore {
    score: number // 0-100
    hasEmail: boolean
    hasPhone: boolean
    hasProfile: boolean
    emailAttempted: boolean
    phoneAttempted: boolean
    profileAttempted: boolean
}

/**
 * Calculate enrichment score based on available data
 * Returns a score from 0-100
 */
export const calculateEnrichmentScore = (
    prospect: Prospect,
): EnrichmentScore => {
    const hasEmail = !!prospect.email.value
    const hasPhone = !!prospect.phone.value
    const hasProfile = !!prospect.linkedinProfile.value
    const emailAttempted = !!prospect.email.last_attempted
    const phoneAttempted = !!prospect.phone.last_attempted
    const profileAttempted = !!prospect.linkedinProfile.last_attempted

    // Calculate score: each contact method is worth points
    // Email: 40 points, Phone: 40 points, Profile: 20 points
    let score = 0
    if (hasEmail) score += 40
    if (hasPhone) score += 40
    if (hasProfile) score += 20

    return {
        score,
        hasEmail,
        hasPhone,
        hasProfile,
        emailAttempted,
        phoneAttempted,
        profileAttempted,
    }
}

/**
 * Determine pipeline stage based on DetailedCampaignProspect status
 */
export const getPipelineStage = (
    campaignProspect: DetailedCampaignProspect,
): PipelineStage => {
    // If they're in a campaign and have status, determine stage
    if (campaignProspect.status === CampaignProspectStatus.COMPLETED) {
        return 'converted'
    }
    if (campaignProspect.status === CampaignProspectStatus.REPLIED) {
        return 'engaged'
    }
    if (campaignProspect.status === CampaignProspectStatus.ACTIVE) {
        return 'engaged'
    }
    if (campaignProspect.status === CampaignProspectStatus.PENDING) {
        return 'prospect'
    }
    return 'prospect'
}

/**
 * Get color for pipeline stage
 */
export const getStatusColor = (stage: PipelineStage): string => {
    switch (stage) {
        case 'prospect':
            return '#6b7280' // gray
        case 'lead':
            return '#3b82f6' // blue
        case 'engaged':
            return '#8b5cf6' // purple
        case 'converted':
            return '#10b981' // green
        default:
            return '#6b7280'
    }
}

/**
 * Get color for enrichment status
 */
export const getEnrichmentColor = (
    hasValue: boolean,
    attempted: boolean,
): string => {
    if (hasValue) return '#10b981' // green - has value
    if (attempted) return '#ef4444' // red - attempted but failed
    return '#9ca3af' // gray - not attempted
}
